import { type FC } from 'react'

import { type SelectFilterOptionValue } from '@/components/select-filter/constants'
import { SelectFilter } from '@/components/select-filter/select-filter'

import { cn } from '@/lib/utils'

import { CasesKeywordFilter } from './keyword-filter/keyword-filter'
import { ResetCasesFilter } from './reset-filter/reset-filter'
import { useCasesFilter } from './use-cases-filter'

export type CasesFilterChangeHandler = (
  params: Pick<
    CasesFilterProps,
    | 'keyword'
    | 'tags'
    | 'tagFilterOption'
    | 'processingTimes'
    | 'processingTimeFilterOption'
    | 'caseTypes'
    | 'statuses'
    | 'assignees'
    | 'assigneeFilterOption'
  >
) => void

export interface CasesFilterProps {
  isEoir?: boolean
  keyword?: string
  tags?: string[]
  tagFilterOption?: SelectFilterOptionValue
  processingTimes?: string[]
  processingTimeFilterOption?: SelectFilterOptionValue
  caseTypes?: string[]
  statuses?: string[]
  assignees?: string[]
  assigneeFilterOption?: SelectFilterOptionValue
  disabled?: boolean
  onChange?: CasesFilterChangeHandler
}

export const CasesFilter: FC<CasesFilterProps> = (props) => {
  const {
    keyword,
    tags,
    tagFilterItems,
    tagFilterOption,
    processingTimes,
    processingTimeFilterItems,
    processingTimeFilterOption,
    caseTypes,
    caseTypeFilterItems,
    statuses,
    statusFilterItems,
    assignees,
    assigneeFilterItems,
    assigneeFilterOption,
    disabled,
    isFilterApplied,
    handleChangeKeyword,
    handleChangeStatuses,
    handleChangeTags,
    handleChangeTagFilterOption,
    handleChangeProcessingTimes,
    handleChangeProcessingTimesFilterOption,
    handleChangeCaseTypes,
    handleChangeAssignees,
    handleChangeAssigneesFilterOption,
    handleReset,
  } = useCasesFilter(props)

  return (
    <div className={cn('flex flex-wrap items-center gap-2')}>
      <CasesKeywordFilter disabled={disabled} keyword={keyword} onChange={handleChangeKeyword} />
      {!props.isEoir && (
        <SelectFilter
          disabled={disabled}
          buttonText="Case Type"
          values={caseTypes}
          items={caseTypeFilterItems}
          onChangeValue={handleChangeCaseTypes}
        />
      )}
      <SelectFilter
        disabled={disabled}
        buttonText="Status"
        values={statuses}
        items={statusFilterItems}
        onChangeValue={handleChangeStatuses}
      />
      <SelectFilter
        disabled={disabled}
        buttonText="Tag"
        values={tags}
        items={tagFilterItems}
        showsFilterOption
        filterOption={tagFilterOption}
        onChangeValue={handleChangeTags}
        onChangeFilterOption={handleChangeTagFilterOption}
      />
      {!props.isEoir && (
        <SelectFilter
          disabled={disabled}
          buttonText="Processing Time"
          values={processingTimes}
          items={processingTimeFilterItems}
          showsFilterOption
          filterOption={processingTimeFilterOption}
          onChangeValue={handleChangeProcessingTimes}
          onChangeFilterOption={handleChangeProcessingTimesFilterOption}
        />
      )}
      <SelectFilter
        disabled={disabled}
        buttonText="Assignee"
        values={assignees}
        items={assigneeFilterItems}
        showsFilterOption
        filterOption={assigneeFilterOption}
        onChangeValue={handleChangeAssignees}
        onChangeFilterOption={handleChangeAssigneesFilterOption}
      />
      {isFilterApplied && <ResetCasesFilter onClick={handleReset} />}
    </div>
  )
}
